import React, { useContext, useEffect, useMemo, useState } from 'react';

import { ZeosSpinner } from '../../../components/Shared/ZeosSpinner';
import { ITableItem } from '../../../components/Shared/ZeosTable/Interfaces';
import { ZeosTable } from '../../../components/Shared/ZeosTable/ZeosTable';
import { ZeosApiContext } from '../../../context/ZeosApiContext';
import {
  incentivesColumns,
  incentivesDataMapper,
} from '../../../mock-data/IncentivesTableData';
import { IAiSummary, IIncentiveContract } from '../../../services/zeos-api/contracts/Incentive';
import { filterRealIncentives } from './filterRealIncentives';
import type { IIncentiveCriterion } from "../../../components/Shared/ZeosTable/Interfaces";

interface IProps {
  incentives: IIncentiveContract[];
  loading: boolean;
  error?: string | null;
  propertyId?: number | null;
  screeningRunGuid?: string | null;
  isScreeningDerived?: boolean;
}

const normalizeCriteria = (value: unknown): IIncentiveCriterion[] => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const raw = item as Record<string, unknown>;
      const resultRaw = raw.result;
      const result =
        typeof resultRaw === "boolean"
          ? resultRaw
          : typeof resultRaw === "string"
          ? resultRaw.toLowerCase() === "true"
          : typeof resultRaw === "number"
          ? resultRaw > 0
          : false;
      const criterion = typeof raw.criterion === "string" ? raw.criterion : "";
      const reason = typeof raw.reason === "string" ? raw.reason : "";
      if (!criterion && !reason) return null;
      return { criterion, result, reason } as IIncentiveCriterion;
    })
    .filter(Boolean) as IIncentiveCriterion[];
};

export const extractCriteriaFromVectorJson = (
  vectorJson?: string
): IIncentiveCriterion[] => {
  if (!vectorJson) return [];

  const tryParse = (json: string): any | null => {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  let parsed = tryParse(vectorJson);
  if (typeof parsed === "string") {
    parsed = tryParse(parsed) ?? parsed;
  }

  if (parsed && typeof parsed === "object") {
    const record = parsed as Record<string, unknown>;
    const criteriaRaw =
      record.criteria_evaluation ?? record.criteriaEvaluation ?? null;
    if (typeof criteriaRaw === "string") {
      const parsedCriteria = tryParse(criteriaRaw);
      if (parsedCriteria) {
        return normalizeCriteria(parsedCriteria);
      }
    }
    const normalized = normalizeCriteria(criteriaRaw);
    if (normalized.length > 0) {
      return normalized;
    }
  }

  const fallbackMatches = Array.from(
    vectorJson.matchAll(
      /"criterion"\s*:\s*"([^"]+)"\s*,\s*"result"\s*:\s*(true|false|1|0)\s*,\s*"reason"\s*:\s*"([^"]*)"/gi
    )
  ).map((match) => ({
    criterion: match[1],
    result: match[2].toLowerCase() === "true" || match[2] === "1",
    reason: match[3],
  }));

  return fallbackMatches;
};

export const IncentivesTable = ({
  incentives,
  loading,
  propertyId,
  screeningRunGuid = null,
  isScreeningDerived = false,
}: IProps) => {
  const api = useContext(ZeosApiContext);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadCategoriesAsync = async () => {
      try {
        const response = await api.Incentive.getCategoriesAsync();
        if (cancelled) {
          return;
        }

        const names = (response.data ?? [])
          .map((category) => category.categoryName?.trim() ?? "")
          .filter((name) => name.length > 0);

        setCategoryFilters(names);
      } catch (err) {
        console.error("Failed to load incentive categories", err);
        if (!cancelled) {
          setCategoryFilters([]);
        }
      }
    };

    loadCategoriesAsync();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const filters = useMemo(
    () => ({ category: categoryFilters }),
    [categoryFilters]
  );

  const mappedIncentives: ITableItem[] = useMemo(() => {
    const buildAiSummary = (incentive: IIncentiveContract): IAiSummary | undefined => {
      const state = (incentive.aiAnalysisState || "none").toLowerCase();
      if (state === "ready" && incentive.aiSummary) {
        const aiSummary = incentive.aiSummary;
        const bullets = aiSummary.bullets?.length
          ? aiSummary.bullets.slice(0, 2).filter(Boolean)
          : [];
        return {
          ...aiSummary,
          bullets,
        };
      }

      if (state === "pending") {
        return {
          shortText:
            "AI analysis is being prepared — refresh this page in a moment.",
        };
      }

      return {
        shortText: "AI analysis not available.",
      };
    };

    return filterRealIncentives(incentives).map((i: IIncentiveContract) => {
      let criteria: IIncentiveCriterion[] = [];
      if (i.incentiveProgramId != -1 && i.vectorJson) {
        const parsedCriteria = extractCriteriaFromVectorJson(i.vectorJson);
        const eligibleCriteria = parsedCriteria.filter((c) => c.result === true);
        criteria = eligibleCriteria.length > 0 ? eligibleCriteria : parsedCriteria;
      }

      if (criteria.length === 0 && i.isManualLink) {
        criteria = [
          {
            criterion: "Manual link",
            result: true,
            reason: "Added manually by an admin.",
          },
        ];
      }

      const displaySummary = buildAiSummary(i);

      const confidenceLabel =
        i.matchConfidence ||
        i.aiSummary?.benefitRange?.confidence ||
        (isScreeningDerived ? "Low" : null);
      const sourceHint = isScreeningDerived
        ? "Preliminary / screening-derived"
        : i.sourceLabel === "VerifiedEligibility"
        ? "Verified eligibility"
        : null;
      // 4037: dataGaps are raw matcher reason tags, not customer-facing copy,
      // so they are no longer surfaced as "Gap: ..." criteria rows.
      const metaBits = [
        sourceHint,
        confidenceLabel ? `Confidence: ${confidenceLabel}` : null,
      ].filter(Boolean) as string[];

      if (metaBits.length > 0) {
        criteria = [
          ...criteria,
          ...metaBits.map((bit) => ({
            criterion: bit,
            result: true,
            reason: "",
          })),
        ];
      }

      const detailParams = new URLSearchParams();
      if (propertyId) {
        detailParams.set("property", String(propertyId));
      }
      if (screeningRunGuid) {
        detailParams.set("screeningRun", screeningRunGuid);
      }
      const detailQuery = detailParams.toString();
      const detailUrl = `/incentives/${i.incentiveId}${
        detailQuery ? `?${detailQuery}` : ""
      }`;

      const baseLabel = i.isManualLink ? `${i.name} (Manual)` : i.name;
      const urlLabel =
        confidenceLabel && isScreeningDerived
          ? `${baseLabel} · ${confidenceLabel}`
          : baseLabel;

      return {
        key: i.incentiveId,
        category: i.category,
        area: i.area,
        incentive: {
          url: detailUrl,
          urlLabel,
        },
        type: i.type,
        deadline: !i.endDate ||
          i.endDate === "1/1/0001" ||
          i.endDate === "01/01/0001"
            ? "Open"
            : i.endDate ?? "",
        provider: i.partnerName || i.provider,
        reasons: criteria,
        aiSummary: displaySummary,
      };
    });
  }, [incentives, isScreeningDerived, propertyId, screeningRunGuid]);

  if (loading) {
    return <ZeosSpinner />;
  } else {
    return (
      <>
        {/* Preserve API ranking order (do not re-sort by category A–Z). */}
        <ZeosTable
          unmappedData={mappedIncentives}
          dataMapping={incentivesDataMapper}
          columnDefs={incentivesColumns}
          searchable={false}
          pagination={true}
          initialOrder={[]}
          filters={filters}
        />
      </>
    );
  }
};
